import { QuestionType } from '@features/event-registration/types';

import { enrichMemberFormFields } from '../components/wizard-steps/fields-step/helpers/memberFieldPicker';
import {
  AddToMode,
  DiscountType,
  FormDetail,
  FormFieldData,
  FormStatus,
  StartCollectionFrom
} from '../types';
import { FormOverviewApiResponse } from '../types/formOverviewApi';
import {
  inferFormFieldCategory,
  normalizeFormFieldId,
  normalizeFormFieldLabel
} from './mapFormApiFields';
import { mapRegistrationPerformance } from './mapRegistrationPerformance';
import { mapSourceBreakdown } from './mapSourceBreakdown';

const normalizeStatus = (status: string): FormStatus => {
  const value = status.toLowerCase();
  if (value === 'paused' || value === 'closed') {
    return value;
  }
  return 'published';
};

const mapAddToMode = (mode: string): AddToMode => {
  const normalized = mode.toLowerCase().replace(/-/g, '_');
  return normalized === 'member_list' ? 'member_list' : 'group';
};

const mapDiscountType = (type: string): DiscountType =>
  type.toLowerCase() === 'flat' ? 'flat' : 'percentage';

const mapFieldType = (type: string): QuestionType => {
  const normalized = type.toLowerCase().replace(/-/g, '_');
  const match = Object.values(QuestionType).find(
    (value) => String(value).toLowerCase() === normalized
  );
  return match ?? QuestionType.TEXT;
};

const mapFormFields = (
  fields: FormOverviewApiResponse['form_fields']
): FormFieldData[] =>
  enrichMemberFormFields(
    [...fields]
      .sort((a, b) => a.display_order - b.display_order)
      .map((field) => {
        const fieldType = mapFieldType(field.type);
        const fieldCategory = inferFormFieldCategory(
          field.name,
          fieldType,
          field.sync_to_member
        );
        return {
          id: normalizeFormFieldId(field.id),
          label: normalizeFormFieldLabel(field.name, fieldType),
          type: fieldType,
          required: field.is_required,
          order: field.display_order,
          fieldCategory,
          options: field.options?.length ? field.options : undefined,
          syncToMember: field.sync_to_member,
          isLocked: fieldCategory === 'basic'
        };
      })
  );

const extractSlugFromFormLink = (formLink: string): string => {
  try {
    const segments = new URL(formLink).pathname.split('/').filter(Boolean);
    return segments[segments.length - 1] ?? '';
  } catch {
    return '';
  }
};

const mapStartCollectionFrom = (
  value: string
): StartCollectionFrom | undefined => {
  const normalized = value?.toLowerCase()?.replace(/-/g, '_') ?? undefined;
  if (normalized === 'same_month' || normalized === 'current_month') {
    return 'same_month';
  }
  if (normalized === 'next_month' || normalized === 'from_next_month') {
    return 'next_month';
  }
  return undefined;
};

const parseOverviewConversionRate = (rate: string | undefined): number => {
  if (!rate?.trim()) {
    return 0;
  }
  const parsed = Number(rate);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const mapFormOverviewToDetail = (
  response: FormOverviewApiResponse
): FormDetail => {
  const addToMode = mapAddToMode(response.add_to_mode);
  const discountEnabled = response.discount_config?.is_enabled ?? false;
  const viewedCount = response.conversion_breakdown?.viewed_count ?? 0;

  return {
    id: response.id,
    name: response.name,
    status: normalizeStatus(response.status),
    addTo: {
      mode: addToMode,
      groupId: response.group?.id,
      groupName: response.group?.name
    },
    registrationCount: response.conversion_breakdown?.paid_count ?? 0,
    viewCount: viewedCount,
    conversionRate: parseOverviewConversionRate(response.conversion_rate),
    lastEdited: response.updated_at,
    description: response.description ?? '',
    feeAmount: response.fee_amount ?? 0,
    acceptInstallments: response.is_partial_payment_enabled,
    minPartialPaymentAmount: response.is_partial_payment_enabled
      ? response.min_partial_payment_amount
      : undefined,
    discountType: discountEnabled
      ? mapDiscountType(response.discount_config.type)
      : undefined,
    discountValue: discountEnabled ? response.discount_config.value : undefined,
    coverImageUrl: response.image_url || undefined,
    slug: extractSlugFromFormLink(response.form_link),
    publicUrl: response.form_link,
    formVersion: response.version,
    autoCloseDate: response.registration_end_time ?? undefined,
    submissionCap: response.registration_max_count || undefined,
    startCollectionFrom: mapStartCollectionFrom(response.start_collection_from),
    fields: mapFormFields(response.form_fields ?? []),
    createdAt: response.created_at,
    sourceTrackingEnabled: response.is_source_tracking_enabled,
    totalRegistrations: response.total_registrations,
    totalRevenue: response.total_revenue,
    totalDiscount: response.total_discount,
    registrationPerformance: mapRegistrationPerformance(
      response.conversion_breakdown
    ),
    sourceBreakdown: mapSourceBreakdown(response.source_breakdown),
    tabCounts: {
      registrations: response.total_registrations ?? 0,
      dropOffs: Math.max(
        0,
        (response.conversion_breakdown?.form_submitted_count ?? 0) -
          (response.conversion_breakdown?.paid_count ?? 0)
      ),
      activity: response.version ?? 0
    }
  };
};

export default mapFormOverviewToDetail;
